<template>
  <aside class="bg-white h-screen overflow-auto">
    <div class="p-4">
      <div class="w-[42rem] space-y-3">
        <transition @enter="enter" @leave="leave">
          <div v-if="requiresAttention" class="overflow-hidden">
            <div class="rounded-md bg-yellow-50 p-4 mb-3">
              <div class="flex">
                <div class="flex-shrink-0">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-6 h-6 text-yellow-400">
                    <path fill-rule="evenodd" d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003zM12 8.25a.75.75 0 01.75.75v3.75a.75.75 0 01-1.5 0V9a.75.75 0 01.75-.75zm0 8.25a.75.75 0 100-1.5.75.75 0 000 1.5z" clip-rule="evenodd" />
                  </svg>
                </div>
                <div class="ml-3 flex-1 md:flex md:justify-between">
                  <p class="text-sm text-yellow-700">
                    There are new keys without translations.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </transition>
        <div class="flex p-4 relative !mt-0">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6">
            <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
          </svg>
          <input
            v-model="search"
            class="outline-none absolute rounded-xl border border-gray-200 h-full w-full left-0 top-0 pl-12 bg-transparent focus:outline-blue-600"
            placeholder="Search key"
            ref="searchEl"
          >
          <div class="absolute mr-4 right-0 top-1/2 -translate-y-1/2 inline-block text-left">
            <div class="relative" @click="showCog" v-click-outside="hideCog">
              <span class="text-gray-500 hover:text-gray-700 cursor-pointer">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M10.343 3.94c.09-.542.56-.94 1.11-.94h1.093c.55 0 1.02.398 1.11.94l.149.894c.07.424.384.764.78.93.398.164.855.142 1.205-.108l.737-.527a1.125 1.125 0 011.45.12l.773.774c.39.389.44 1.002.12 1.45l-.527.737c-.25.35-.272.806-.107 1.204.165.397.505.71.93.78l.893.15c.543.09.94.56.94 1.109v1.094c0 .55-.397 1.02-.94 1.11l-.893.149c-.425.07-.765.383-.93.78-.165.398-.143.854.107 1.204l.527.738c.32.447.269 1.06-.12 1.45l-.774.773a1.125 1.125 0 01-1.449.12l-.738-.527c-.35-.25-.806-.272-1.203-.107-.397.165-.71.505-.781.929l-.149.894c-.09.542-.56.94-1.11.94h-1.094c-.55 0-1.019-.398-1.11-.94l-.148-.894c-.071-.424-.384-.764-.781-.93-.398-.164-.854-.142-1.204.108l-.738.527c-.447.32-1.06.269-1.45-.12l-.773-.774a1.125 1.125 0 01-.12-1.45l.527-.737c.25-.35.273-.806.108-1.204-.165-.397-.505-.71-.93-.78l-.894-.15c-.542-.09-.94-.56-.94-1.109v-1.094c0-.55.398-1.02.94-1.11l.894-.149c.424-.07.765-.383.93-.78.165-.398.143-.854-.107-1.204l-.527-.738a1.125 1.125 0 01.12-1.45l.773-.773a1.125 1.125 0 011.45-.12l.737.527c.35.25.807.272 1.204.107.397-.165.71-.505.78-.929l.15-.894z" />
                  <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </span>

              <div
                class="pointer-events-auto absolute mt-2 opacity-0 invisible transition right-0 z-10 mt-2 w-56 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                :class="showingCogDropdown && '!mt-0 !opacity-100 !visible'"

                role="menu"
                aria-orientation="vertical"
                aria-labelledby="menu-button"
                tabindex="-1"
              >
                <div class="py-1" role="none">
                  <!-- Active: "bg-gray-100 text-gray-900", Not Active: "text-gray-700" -->
                  <span class="text-gray-700 group cursor-pointer flex items-center px-4 py-2 text-sm" role="menuitem" tabindex="-1" id="menu-item-0" @click="copyKeysAsTranslatableList">
                    <!-- Heroicon name: mini/pencil-square -->
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500">
                      <path fill-rule="evenodd" d="M10.5 3A1.501 1.501 0 009 4.5h6A1.5 1.5 0 0013.5 3h-3zm-2.693.178A3 3 0 0110.5 1.5h3a3 3 0 012.694 1.678c.497.042.992.092 1.486.15 1.497.173 2.57 1.46 2.57 2.929V19.5a3 3 0 01-3 3H6.75a3 3 0 01-3-3V6.257c0-1.47 1.073-2.756 2.57-2.93.493-.057.989-.107 1.487-.15z" clip-rule="evenodd" />
                    </svg>
                    Join keys and copy it as long string
                  </span>
                  <span class="text-gray-700 group cursor-pointer flex items-center px-4 py-2 text-sm" role="menuitem" tabindex="-1" id="menu-item-1" @click="pasteList">
                    <!-- Heroicon name: mini/document-duplicate -->
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500">
                      <path d="M11.47 1.72a.75.75 0 011.06 0l3 3a.75.75 0 01-1.06 1.06l-1.72-1.72V7.5h-1.5V4.06L9.53 5.78a.75.75 0 01-1.06-1.06l3-3zM11.25 7.5V15a.75.75 0 001.5 0V7.5h3.75a3 3 0 013 3v9a3 3 0 01-3 3h-9a3 3 0 01-3-3v-9a3 3 0 013-3h3.75z" />
                    </svg>
                    Upload translated long string
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="overflow-hidden border border-gray-200 ring-1 ring-black ring-opacity-5 md:rounded-lg">
          <table class="min-w-full table-fixed divide-y divide-gray-200">
            <thead class="bg-gray-50">
            <tr class="divide-x divide-gray-200">
              <th scope="col" class="w-1/2 py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-5">Not translated Key</th>
              <th scope="col" class="w-1/2 py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-5">Translated value</th>
            </tr>
            </thead>
            <!-- Odd row -->
            <tbody class="bg-white">
            <tr
              v-for="key in (loading ? 13 : filteredKeys)"
              :key="key"

              class="divide-x divide-gray-200 w-full duration-100 overflow-hidden"
            >
              <td class="border-t border-gray-100 py-3.5 pl-4 text-sm font-medium text-gray-900 sm:pl-5 max-w-[6.25rem]">
                <span :class="loading && 'ske'">
                  {{ loading ? 'aishoten ashitneasihotn thneos' : key }}
                </span>
              </td>
              <td
                class="border-t !border-t-gray-100 text-sm text-gray-500 max-w-[6.25rem]"
                :class="[
                  loading && 'px-4 py-3.5 sm:pl-5',
               ]"
              >
                <input
                  v-if="!loading"
                  class="px-4 py-3.5 sm:pl-5 w-full outline-blue-600"

                  v-model="translationUpdates[key]"
                  @keydown="translationInput"
                >
                <span v-else class="ske">ashioet thaeos ashte</span>
              </td>
            </tr>
            </tbody>

            <!-- More people... -->
          </table>
        </div>
      </div>
    </div>
  </aside>
</template>

<script>

import { mapGetters } from "vuex"

import TransitionFunctionConstructor from "@/mixins/transitionFunctionConstructor"

let timeout

export default {
  mixins: [TransitionFunctionConstructor()],
  props: ['loading', 'keys', 'requiresAttention', 'initialTranslations'],
  data() {
    return {
      search: "",

      // ...this.keys.reduce((all, key) => ({ [key]: '' })
      initialKeys: null,
      translationUpdates: {...this.initialTranslations || {}},
      showingCogDropdown: false,
      initialKeysList: null,
    }
  },
  created() {
    this.initialKeys = {...this.translationUpdates}
    this.translationUpdates = {...this.initialTranslations}
  },
  mounted() {
    setTimeout(() => this.$refs.searchEl.focus(), 200)
  },
  methods: {
    showCog() {
      requestAnimationFrame(() => this.showingCogDropdown = true)
    },
    hideCog() {
      this.showingCogDropdown = false
    },
    copyKeysAsTranslatableList() {
      navigator.clipboard.writeText(this.keys.join('|||'))
      this.$store.dispatch("toast/success", { message: "Successfully copied keys!" })
    },
    async pasteList() {
      const keys = this.keys

      const translatedKeys = (await navigator.clipboard.readText())
        .replace(/\|\s\|\|/g, "|||")
        .replace(/\|\|\s\|/g, "|||")

      const splitted = translatedKeys.split('|||')
      let index = 0
      let missedOnes = 0
      for (const key of splitted) {
        const initialKey = keys[index + missedOnes]

        // Translations with variables should be put on the list. E.g: {{stock}} items left
        // Translations that are not exactly transtlosnh should not be put as well. E.g: [CONTACT_EMAIL]
        if (/{{.*}}/g.test(initialKey) || /\[.*\]/g.test(initialKey)) {
          index++
          continue;
        }
        this.translationUpdates[initialKey] = key
        index++
      }
      this.translationUpdates = {...this.translationUpdates}
      this.translationInput()
      this.$store.dispatch("toast/success", { message: "Successfully uploaded translation!" })
    },
    updateGlobalTranslation() {
      const updates = {...this.translationUpdates}
      const keys = this.keys

      const toDelete = []
      Object.keys(updates).forEach(key => {
        if (!keys.includes(key) || !updates[key]) toDelete.push(key)
      })
      // toDelete.forEach(key => delete updates[key])

      this.$store.dispatch("editor/overwriteConfig", {path: "translations", value: updates})
    },
    translationInput() {
      if (timeout) {
        clearTimeout(timeout)
        timeout = null
      }

      timeout = setTimeout(() => this.updateGlobalTranslation(), 1000)
    },
  },
  computed: {
    ...mapGetters({
      ready: "editor/ready",
    }),

    /*hasUpdatedKeys() {
      const updates = []
      const translations = this.globalTranslations
      const translationUpdates = this.translationUpdates

      Object.keys(translationUpdates).forEach(key => {
        if (!translations[key] || translations[key] !== translationUpdates[key]) {
          updates.push(key)
        }
      })
      return updates
    },*/
    hasNotAdddedAnyKeysYet() {
      return this.initialTranslations === null
    },
    filteredKeys() {
      const search = this.search
      const translations = this.translationUpdates
      const keys = search
        ? this.keys.filter(key => {
          if (translations[key] && translations[key].toLowerCase().includes(search.toLowerCase())) return true
          return key.toLowerCase().includes(search.toLowerCase())
        })
        : this.keys.slice()

      const initialKeys = this.initialKeys
      keys.sort((a, b) => {
        if (initialKeys[a] && !initialKeys[b]) return 1
        if (!initialKeys[a] && initialKeys[b]) return -1
        return a.localeCompare(b)
      })
      return keys
    },
  },
  watch: {
    loading() {
      this.translationUpdates = {...this.translationUpdates}
    },
    ready() {
      this.$nextTick(() => {
        this.translationUpdates = {...this.translationUpdates}
      })
    },
  },
}

</script>
